import React, { useState, useContext, lazy, Suspense, useCallback } from "react";
import { GridFooter, GridFooterContainer } from "@mui/x-data-grid";
import { Button, Card, ButtonGroup, Typography, Dialog } from "@mui/material";
import Constants from "../../services/Constants";
import { getColumnsData } from "./VehicleColumns";
import {
  getActiveVehiclesByQuery,
  getVehicleData,
} from "../../services/deployment";
import { useQuery } from "react-query";
import { WizardContext, ACTIONS, TABS } from "../wizard/Context";
import Check from "../../auth/ability";
import { CAN, SUBJECTS } from "../../auth/permissions";
import OTAPagination from "../OTAPagination";
import { CreateVehicleGroupSkeleton } from "../common/DialogSkeleton";
const CreateVehicleGroup = lazy(() => import("./group/CreateVehicleGroup"));

const CustomFooter = ({ onSearchResult, onSelectedVehicles, selectedCount }) => (
  <>
    <GridFooterContainer>
      <div style={{ marginLeft: "1em" }}>
        <label>
          <Typography variant="button">Create group from :</Typography>
        </label>
        <ButtonGroup
          variant="contained"
          aria-label="outlined primary button group"
          sx={{ ml: 1 }}
        >
          <Button variant="outlined" size="small" onClick={onSearchResult}>
            Search result
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={selectedCount < 1}
            onClick={onSelectedVehicles}
          >
            {`Selected Vehicles (${selectedCount})`}
          </Button>
        </ButtonGroup>
      </div>
      <GridFooter />
    </GridFooterContainer>
  </>
);

const VehicleList = ({
  vehicleId,
  groupItemsDataArray,
  selectedGroupsItems,
  extraFilters,
  selectedVehiclesSerials,
  setSelectedVehiclesSerials,
  selectedVehiclesList,
  setSelectedVehiclesList,
}) => {
  const [openModal, setOpenModal] = useState(null);
  const [fetchData, setFetchData] = useState(false);
  const [originalRowsData, setOriginalRowsData] = useState([]);

  const { dispatch } = useContext(WizardContext);
  const [pageSize, setPageSize] = useState(Constants.PAGE_SIZE);
  const [mainList, setMainList] = useState([]);

  const { data: activeVehiclesResponse, refetch: refetchActiveVehicles } = useQuery(
    [
      "getVehiclesForGroupingByQuery",
      1,
      100,
      "serialNoCreatedAt",
      "DESC",
      extraFilters ? extraFilters : [],
    ],
    getActiveVehiclesByQuery,
    {
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

  const handleOpen = (items) => {
    setOpenModal({
      createGroupFromSelectedItems: true,
      items: items?.length ? items : [],
    });
  };

  const handleSearchResultClick = useCallback(async () => {
    const response = await refetchActiveVehicles();
    const result = response?.data?.data?.res ?? [];
    handleOpen(result);
  }, [refetchActiveVehicles]);

  const handleSelectedVehiclesClick = useCallback(() => {
    handleOpen(selectedVehiclesList);
  }, [selectedVehiclesList]);

  const onVehiclesSelected = (items) => {
    setSelectedVehiclesSerials(items);
    const list = items?.map((i) =>
      [...originalRowsData, ...selectedVehiclesList]?.find(
        (el) => el?.serialNo === i
      )
    );
    setSelectedVehiclesList(list);
  };

  const columns = getColumnsData();

  const FooterComponent = useCallback(
    () => (
      <CustomFooter
        onSearchResult={handleSearchResultClick}
        onSelectedVehicles={handleSelectedVehiclesClick}
        selectedCount={selectedVehiclesSerials?.length ?? 0}
      />
    ),
    [handleSearchResultClick, handleSelectedVehiclesClick, selectedVehiclesSerials?.length]
  );

  return (
    <Card>
      <OTAPagination
        exportRowsData={(d) => {
          setOriginalRowsData(d);
        }}
        extraFilters={extraFilters}
        columns={columns}
        allVehicles
        endpointFn={getVehicleData}
        queryName={"vehicles"}
        filterMode="server"
        sortByDefault={"serialNoCreatedAt"}
        getRowId={(row) => row.serialNo}
        sx={{ height: "calc(100vh - 200px)" }}
        checkboxSelection={true}
        isRowSelectable={(params) =>
          params?.row?.active && params?.row?.certAvailable
        }
        onSelectionModelChange={onVehiclesSelected}
        selectionModel={selectedVehiclesSerials}
        keepNonExistentRowsSelected
        components={{ Footer: FooterComponent }}
        exportCSV
      />
      {openModal && (
        <Dialog
          open={openModal}
          onClose={() => setOpenModal(!openModal)}
          PaperProps={{
            sx: { width: "50%", maxWidth: "1200px", height: "calc(100vh - 30px)" },
          }}
        >
          <Suspense fallback={<CreateVehicleGroupSkeleton/>}>
            <CreateVehicleGroup
              open={openModal}
              toggleDialog={() => setOpenModal(!openModal)}
            />
          </Suspense>
        </Dialog>
      )}
    </Card>
  );
};

export default VehicleList;
